import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';
import { AccountBudgetTypeMapping } from 'src/models/xPTMappingModels';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';

/**
 * Service for validating budget type and account combinations in forecast templates
 */
export class BudgetTypeValidationService extends ValidationService {
  /**
   * Validates that the budget type and account combinations are valid.
   * @param forecastTemplateRelevantRowData The array of relevant rows to validate.
   * @param accountBudgetTypeMapping The mapping of accounts to budget types.
   * @returns Promise with validation status
   */
  public async validateBudgetTypeAndAccountCombination(
    forecastTemplateRelevantRowData: any[],
    accountBudgetTypeMapping: AccountBudgetTypeMapping[]
  ): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];

      logger.info(`Validating budget type and account combination for ${forecastTemplateRelevantRowData.length} records`);

      forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
        const accountCode = row[CorpSegmentNames.ACCOUNT];
        const budgetType = row[ForecastGridFixedFields.BudgetType.value];

        const accountBudgetTypeEntry = accountBudgetTypeMapping.find((entry) => entry.account_code === accountCode);

        if (!accountBudgetTypeEntry) {
          validationErrorDetails.push({
            rowIndex: rowIndex + 1,
            message: `Account ${accountCode} not found in the mapping ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )}`
          });
        }

        if (accountBudgetTypeEntry && accountBudgetTypeEntry.budget_type !== budgetType) {
          validationErrorDetails.push({
            rowIndex: rowIndex + 1,
            message: `Budget Type ${budgetType} is not valid for Account ${accountCode} ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )}`
          });
        }
      });

      // Log validation errors if any
      this.logValidationErrors('Budget Type & Account Mapping', validationErrorDetails);

      // Return appropriate validation status
      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_FAILED,
          ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_SUCCESS,
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Budget Type & Account Mapping:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_ERROR,
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget type and account combination' }]
      );
    }
  }
}

// Export singleton instance
export const budgetTypeValidationService = new BudgetTypeValidationService();
